'use client';

import '../store/modules/index';
import '../store/modules/agent-store';
import { useInitial } from '@/components/use-initial';
import { ThemeProvider } from 'next-themes';
import { useState, createContext, useEffect } from 'react';
import { ReactQueryProvider } from '@/providers/react-query-provider';
import { dehydrate, QueryClient } from '@tanstack/react-query';
import { ToolSettingsInitializer } from '@/components/tool-settings-initializer';
import useApiNotify from '@/hooks/deep-mining/use-api-notify';
import ImagePreview from '@/components/deep-mining/image-preview';
import TemplateDetailModal from '@/components/deep-mining/template/template-detail-modal';
import { AddCustomerModalWrapper } from '@/components/deep-mining/add-customer-modal';
import { initiate as initMonitor, updateConfig } from '@/lib/monitor';
import WorkerDetailModal from '@/components/thread/chat-input/worker-detail-modal';
import { ThreadModelProvider } from '@/contexts/thread-model-context';
import { App } from 'antd';
import PptPlayModal from '@/components/ppt-play-modal';
import { ShareModalWrapper } from '@/components/deep-mining/share-modal';
import useGlobalModal from '@/hooks/use-global-modal';
import { GlobalConfigProvider } from '@/contexts/global-config-context';
import { AntdThemeProvider } from '@/lib/theme-wrapper/antd-theme-provider';
import { GlobalModalContainer } from '@/components/antd-wrapper/modal/global';
import { CustomerModalContainer } from '@/components/antd-wrapper/modal/contact/container';
import { IconProvider } from '@/contexts/IconContext';
import TopTipMessage from '@/components/deep-mining/top-tip-message';
import { DeviceConnectionModel } from '@/components/device-connection-model';
import UploadSkillModal from '@/components/upload-skill-modal';
import { useStores } from '@/store/hooks/useStore';
import { I18nProvider } from '@/providers/i18n-provider';
import { SkillDetailModalWrapper } from '@/components/skill-detail-model';
import { SkillFileModalWrapper } from '@/components/skill-file-model';
import FileTaskDownloadModalWrapper from '@/components/thread/video-task-download/file-task-download-modal';

export interface ParsedTag {
  tagName: string;
  attributes: Record<string, string>;
  content: string;
  isClosing: boolean;
  id: string;
  rawMatch?: string;
  timestamp?: number;

  resultTag?: ParsedTag;
  isToolCall?: boolean;
  isPaired?: boolean;
  status?: 'running' | 'completed' | 'error';

  vncPreview?: string;
}

initMonitor();

export const ToolCallsContext = createContext<{
  toolCalls: ParsedTag[];
  setToolCalls: React.Dispatch<React.SetStateAction<ParsedTag[]>>;
}>({
  toolCalls: [],
  setToolCalls: () => {},
});

const ApiNotifyContainer = () => {
  useApiNotify();
  return null;
};

const GlobalModalContainer_ = () => {
  useGlobalModal();
  return null;
};

export function Providers({ children }: { children: React.ReactNode }) {
  const [toolCalls, setToolCalls] = useState<ParsedTag[]>([]);
  const queryClient = new QueryClient();
  const dehydratedState = dehydrate(queryClient);
  const stores = useStores();
  useInitial();

  useEffect(() => {
    const userId = stores.userStore.userInfo?.user_id;
    if (!userId) return;
    updateConfig({ uin: userId });
  }, [stores?.userStore?.userInfo?.user_id]);

  return (
    <GlobalConfigProvider>
      <I18nProvider>
        <ToolCallsContext.Provider value={{ toolCalls, setToolCalls }}>
          <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
            <AntdThemeProvider>
              <App>
                <ReactQueryProvider dehydratedState={dehydratedState}>
                  <ToolSettingsInitializer />
                    <ThreadModelProvider>
                    <IconProvider>{children}</IconProvider>
                    <ImagePreview />
                    <ApiNotifyContainer />
                    {/* old Modal */}
                    <GlobalModalContainer_ />
                    {/* new Modal */}
                    <GlobalModalContainer></GlobalModalContainer>
                    <CustomerModalContainer></CustomerModalContainer>
                    <TemplateDetailModal />
                    <ShareModalWrapper />
                    <WorkerDetailModal />
                    <PptPlayModal />
                    <AddCustomerModalWrapper />
                    <TopTipMessage />
                    <DeviceConnectionModel />
                    <UploadSkillModal />
                    <SkillDetailModalWrapper />
                    <SkillFileModalWrapper />
                    <FileTaskDownloadModalWrapper />
                    </ThreadModelProvider>
                </ReactQueryProvider>
              </App>
            </AntdThemeProvider>
          </ThemeProvider>
        </ToolCallsContext.Provider>
      </I18nProvider>
    </GlobalConfigProvider>
  );
}
